/**
 * PortalMessagePresetsApi — one call: the office's saved passenger messages,
 * for the composer on the dispatcher's trip screen.
 *
 *   GET /portal/message-presets?booking_id=…
 *
 * Built on the portal's own client (`portalClient` + `payloadOf` from
 * PortalApi.js), exactly like PortalMessageApi beside it — so the portal
 * token, the silent refresh and the "never send the admin token" guarantee
 * all come from there.
 *
 * NEVER `Services/API`. That module installs its interceptor on the DEFAULT
 * axios instance, which attaches the admin bearer token from
 * localStorage.SESSION_TOKEN to every call made through it. A portal screen
 * must not send an admin credential, and the hazard is not hypothetical: a
 * browser where somebody signed into the dashboard earlier still has that
 * key, so a DRIVER's portal session would send a stale admin token. The
 * portal's client only ever sends what the portal session itself stored.
 *
 * `MessagePresetsService` is the dashboard's client for the same library and
 * goes through `Services/API`, which is why the portal could not simply
 * reuse it and why this file exists. BOTH END AT ONE SERVICE AND ONE
 * COLLECTION on the server (`MessagePresetService`); what differs is the
 * authorizer the request passes and the token it carries, not the data.
 *
 * ONE READ, BOTH CHANNELS. The endpoint can filter (`?channel=`) but this
 * deliberately does not ask it to: the payload carries each row's channel,
 * so the composer's toggle swaps the chip row in the browser
 * (`Utils/messageChannel.presetsForChannel`) with no second round trip —
 * which is what a toggle on a phone at the kerb needs. It is also what the
 * dashboard's composer does, so the two behave the same way.
 *
 * SCOPED TO THE BOOKING, always. The booking-scoped read is the only one a
 * composer can use: the per-partner free-waiting allowances in a message
 * like "at the airport we have 60 minutes" are a sales channel's commercial
 * terms, resolved from the booking's partner on the server. The browser has
 * no way to know them and must not guess — an unscoped read would hand the
 * composer `{airport_minutes}` to publish to a customer verbatim.
 */
import { payloadOf, portalClient } from './PortalApi';

/**
 * The saved messages for one booking, with their variables already filled
 * in, plus the ones that could not be filled and why.
 *
 * Returns `{ presets, omitted }`, each defaulting to an empty array so a
 * caller never has to guard a missing key. `variables` is deliberately
 * dropped: it is the vocabulary for the dashboard's MANAGEMENT screen, and
 * the portal has no screen that edits a preset.
 *
 * EVERY ROW CARRIES A `channel`, `omitted` included, so one filter serves
 * both lists. A row with no channel is a WhatsApp one — the rows that
 * predate the field were all written for the WhatsApp template, and the API
 * coerces from its side so the browser is agreeing rather than guessing.
 */
export const fetchPassengerMessagePresets = async (bookingId) => {
  const payload = payloadOf(await portalClient.get('/portal/message-presets', {
    params: { booking_id: bookingId },
  }));

  return {
    presets: payload?.presets || [],
    omitted: payload?.omitted || [],
  };
};

export default fetchPassengerMessagePresets;
